// src/components/ServersParser/ServersParserConfigCard.jsx
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const ServersParserConfigCard = ({
  searchType,
  onSearchTypeChange,
  selectedVuln,
  onSelectVuln,
  playersMin,
  playersMax,
  onPlayersMinChange,
  onPlayersMaxChange,
  versionFilter,
  onVersionFilterChange,
  serverLimit,
  onServerLimitChange,
  onStartParsing,
  isProcessing
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  return (
    <aside className="sp-config-card">
      {/* Заголовок */}
      <div className="sp-config-card-heading">
        <svg viewBox="0 0 24 24">
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
        <h3 className="sp-config-card-title">
          {isEn ? 'Configuration' : 'Конфигурация'}
        </h3>
      </div>

      {/* 1. ТИП ПОИСКА */}
      <div className="sp-field-group">
        <div className="sp-field-label-row">
          <span className="sp-field-label">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
            <span>{isEn ? 'Search Type' : 'Тип поиска'}</span>
          </span>
        </div>

        <div className="sp-type-segment-pill">
          <button
            type="button"
            className={`sp-type-btn ${searchType === 'vuln' ? 'active' : ''}`}
            onClick={() => onSearchTypeChange('vuln')}
          >
            {isEn ? 'By Vulnerability' : 'По уязвимости'}
          </button>
          <button
            type="button"
            className={`sp-type-btn ${searchType === 'params' ? 'active' : ''}`}
            onClick={() => onSearchTypeChange('params')}
          >
            {isEn ? 'By Parameters' : 'По параметрам'}
          </button>
        </div>
      </div>

      {/* 2A. УРОВЕНЬ УЯЗВИМОСТИ */}
      {searchType === 'vuln' ? (
        <div className="sp-field-group">
          <div className="sp-field-label-row">
            <span className="sp-field-label">
              <svg viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>{isEn ? 'Vulnerability Level' : 'Уровень уязвимости'}</span>
            </span>
          </div>

          <div className="sp-vuln-cards-stack">
            {/* Низкая */}
            <div
              className={`sp-vuln-item-card ${selectedVuln === 'low' ? 'selected' : ''}`}
              onClick={() => onSelectVuln('low')}
            >
              <div className="sp-vuln-item-content">
                <div className="sp-vuln-item-title-row">
                  <span className="sp-vuln-item-title">
                    {isEn ? 'Low Vulnerability' : 'Низкая уязвимость'}
                  </span>
                </div>
                <div className="sp-vuln-item-sub">
                  {isEn ? 'Servers with presumably basic vulnerabilities' : 'Серверы с предположительно базовыми уязвимостями'}
                </div>
              </div>
              <div className="sp-vuln-radio-dot">
                <svg viewBox="0 0 24 24" fill="none">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>

            {/* Средняя */}
            <div
              className={`sp-vuln-item-card ${selectedVuln === 'medium' ? 'selected' : ''}`}
              onClick={() => onSelectVuln('medium')}
            >
              <div className="sp-vuln-item-content">
                <div className="sp-vuln-item-title-row">
                  <span className="sp-vuln-item-title">
                    {isEn ? 'Medium Vulnerability' : 'Средняя уязвимость'}
                  </span>
                </div>
                <div className="sp-vuln-item-sub">
                  {isEn ? 'Servers with presumably medium vulnerabilities' : 'Серверы с предположительно средними уязвимостями'}
                </div>
              </div>
              <div className="sp-vuln-radio-dot">
                <svg viewBox="0 0 24 24" fill="none">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>

            {/* Высокая */}
            <div
              className={`sp-vuln-item-card ${selectedVuln === 'high' ? 'selected' : ''}`}
              onClick={() => onSelectVuln('high')}
            >
              <div className="sp-vuln-item-content">
                <div className="sp-vuln-item-title-row">
                  <span className="sp-vuln-item-title">
                    {isEn ? 'High Vulnerability' : 'Высокая уязвимость'}
                  </span>
                  <span className="sp-vip-badge-tag">VIP+</span>
                </div>
                <div className="sp-vuln-item-sub">
                  {isEn ? 'Servers with critical vulnerabilities (VIP tier)' : 'Серверы с предположительно критическими уязвимостями (тариф VIP и выше)'}
                </div>
              </div>
              <div className="sp-vuln-radio-dot">
                <svg viewBox="0 0 24 24" fill="none">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* 2B. РУЧНЫЕ ПАРАМЕТРЫ */
        <div className="sp-field-group">
          <div className="sp-field-label-row">
            <span className="sp-field-label">
              <svg viewBox="0 0 24 24">
                <line x1="4" x2="20" y1="12" y2="12" />
                <line x1="4" x2="20" y1="6" y2="6" />
                <line x1="4" x2="20" y1="18" y2="18" />
              </svg>
              <span>{isEn ? 'Filter Parameters' : 'Параметры фильтрации'}</span>
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Диапазон онлайна */}
            <label className="sp-filter-input-capsule" htmlFor="spParamPlayersMin">
              <div className="sp-filter-range-split">
                <span style={{ fontSize: '0.76rem', color: 'var(--sp-text-dimmed)', whiteSpace: 'nowrap' }}>
                  {isEn ? 'Players:' : 'Игроки:'}
                </span>
                <input
                  type="number"
                  id="spParamPlayersMin"
                  placeholder={isEn ? 'From' : 'От'}
                  min="0"
                  value={playersMin}
                  onChange={(e) => onPlayersMinChange(e.target.value)}
                />
                <span className="sp-filter-range-sep">—</span>
                <input
                  type="number"
                  id="spParamPlayersMax"
                  placeholder={isEn ? 'To' : 'До'}
                  min="0"
                  value={playersMax}
                  onChange={(e) => onPlayersMaxChange(e.target.value)}
                />
              </div>
            </label>

            {/* Фильтр версий */}
            <label className="sp-filter-input-capsule" htmlFor="spParamVersionInput">
              <span style={{ fontSize: '0.76rem', color: 'var(--sp-text-dimmed)', whiteSpace: 'nowrap' }}>
                {isEn ? 'Version:' : 'Версия:'}
              </span>
              <input
                type="text"
                id="spParamVersionInput"
                placeholder={isEn ? 'e.g. 1.16.5, 1.20' : 'напр. 1.16.5, 1.20'}
                autoComplete="off"
                spellCheck="false"
                value={versionFilter}
                onChange={(e) => onVersionFilterChange(e.target.value)}
              />
            </label>
          </div>
        </div>
      )}

      {/* 3. КОЛИЧЕСТВО СЕРВЕРОВ */}
      <div className="sp-field-group">
        <div className="sp-field-label-row">
          <span className="sp-field-label">
            <svg viewBox="0 0 24 24">
              <rect x="2" y="2" width="20" height="8" rx="2" />
              <rect x="2" y="14" width="20" height="8" rx="2" />
            </svg>
            <span>{isEn ? 'Server Count' : 'Количество серверов'}</span>
          </span>
        </div>

        <div className="sp-count-pills-row">
          {[10, 30, 50].map((limit) => (
            <button
              key={limit}
              type="button"
              className={`sp-count-btn ${serverLimit === limit ? 'active' : ''}`}
              onClick={() => onServerLimitChange(limit)}
            >
              {limit}
            </button>
          ))}
        </div>
      </div>

      {/* 4. КНОПКА ЗАПУСКА */}
      <button
        type="button"
        className="sp-start-parse-btn"
        onClick={onStartParsing}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <div className="sp-btn-spinner" />
            <span>{isEn ? 'Parsing in progress...' : 'Идет сбор...'}</span>
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="none">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            <span>{isEn ? 'Start parsing' : 'Запустить парсинг'}</span>
          </>
        )}
      </button>
    </aside>
  );
};

export default ServersParserConfigCard;
